import React, { useState, useEffect } from 'react';
import { Clock, Plus, Edit2, Trash2, Users, List, LayoutGrid, X, Save, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import shiftService, { Shift, ShiftCreateDto, ShiftUpdateDto, ShiftEmployee } from '../services/shiftService';

const Shifts = () => {
  const { t } = useTranslation();
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingShift, setEditingShift] = useState<Shift | null>(null);
  const [formData, setFormData] = useState<ShiftCreateDto>({
    name: '',
    startTime: '08:00',
    endTime: '17:00',
    workHours: 8,
    breakMinutes: 60,
    crossesMidnight: false,
    restDayCount: 0,
  });

  // Employee modal
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [selectedShift, setSelectedShift] = useState<Shift | null>(null);
  const [shiftEmployees, setShiftEmployees] = useState<ShiftEmployee[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingShift, setDeletingShift] = useState<Shift | null>(null);

  useEffect(() => {
    loadShifts();
  }, []);

  const loadShifts = async () => {
    try {
      setLoading(true);
      const response = await shiftService.getAll();
      if (response.success && response.data) {
        setShifts(response.data);
      } else {
        setError(response.message || t('shiftsPage.loadError'));
      }
    } catch (err) {
      setError(t('shiftsPage.serverError'));
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return '--:--';
    // Handle TimeSpan format from .NET (HH:mm:ss)
    const parts = timeStr.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  // Calculate work hours from start and end time
  const calculateWorkHours = (startTime: string, endTime: string): number => {
    if (!startTime || !endTime) return 0;

    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);

    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;

    // Handle overnight shifts (e.g., 23:00 - 07:00)
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    const diffMinutes = endMinutes - startMinutes;
    return Math.round((diffMinutes / 60) * 10) / 10; // Round to 1 decimal
  };

  // 2026-08-14: Calisma suresini DAKIKA olarak hesapla (yuvarlamasiz).
  // WorkHours INT oldugu icin 10,5 saatlik vardiya 11'e yuvarlanip puantajda
  // 11:00 gorunuyordu. Artik dakika gonderiyoruz, mola da dusuluyor.
  const calculateWorkMinutes = (startTime: string, endTime: string, breakMinutes: number): number => {
    if (!startTime || !endTime) return 0;
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    let bas = sh * 60 + sm;
    let bit = eh * 60 + em;
    if (bit <= bas) bit += 24 * 60;          // gece vardiyasi
    const brut = bit - bas;
    const net = brut - (breakMinutes || 0);
    return net > 0 ? net : brut;             // mola brutu asiyorsa brut kalsin
  };

  // Format duration as hours and minutes
  const formatDuration = (startTime: string, endTime: string): string => {
    if (!startTime || !endTime) return '-';

    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);

    let startMinutes = startH * 60 + startM;
    let endMinutes = endH * 60 + endM;

    // Handle overnight shifts
    if (endMinutes <= startMinutes) {
      endMinutes += 24 * 60;
    }

    const diffMinutes = endMinutes - startMinutes;
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (minutes === 0) {
      return `${hours} ${t('shiftsPage.hours')}`;
    }
    return `${hours} ${t('shiftsPage.hours')} ${minutes} ${t('shiftsPage.minutes')}`;
  };

  // Update workHours when time changes
  const handleTimeChange = (field: 'startTime' | 'endTime', value: string) => {
    const newFormData = { ...formData, [field]: value };
    const start = field === 'startTime' ? value : formData.startTime;
    const end = field === 'endTime' ? value : formData.endTime;
    newFormData.workHours = calculateWorkHours(start, end);
    setFormData(newFormData);
  };

  const openAddModal = () => {
    setEditingShift(null);
    setFormData({
      name: '',
      startTime: '08:00',
      endTime: '17:00',
      workHours: 8,
      breakMinutes: 60,
      crossesMidnight: false,
      restDayCount: 0,
    });
    setShowModal(true);
  };

  const openEditModal = (shift: Shift) => {
    setEditingShift(shift);
    setFormData({
      name: shift.name,
      startTime: formatTime(shift.startTime),
      endTime: formatTime(shift.endTime),
      workHours: shift.workHours || 8,
      breakMinutes: (shift as any).breakMinutes ?? 60,
      crossesMidnight: (shift as any).crossesMidnight ?? false,
      restDayCount: (shift as any).restDayCount ?? 0,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingShift) {
        // Backend workHours int? bekliyor — float ondalik gonderirse JSON parse hatasi
        // 9.5 gibi degerler icin backend zaten StartTime-EndTime'dan yeniden hesaplar
        const updateData: ShiftUpdateDto = {
          ...formData,
          workHours: Math.round(formData.workHours || 0),
          // Kesirli sureler icin dakika degeri — backend bunu oncelikli kullanir
          workMinutes: calculateWorkMinutes(formData.startTime, formData.endTime, formData.breakMinutes ?? 60),
          breakMinutes: Math.round(formData.breakMinutes ?? 60),
          restDayCount: Math.round(formData.restDayCount ?? 0),
          isActive: editingShift.isActive,
        };
        const response = await shiftService.update(editingShift.id, updateData);
        if (response.success) {
          setShowModal(false);
          loadShifts();
        } else {
          setError(response.message || t('shiftsPage.updateError'));
        }
      } else {
        const createData: any = {
          ...formData,
          workHours: Math.round(formData.workHours || 0),
          // Kesirli sureler icin dakika degeri — backend bunu oncelikli kullanir
          workMinutes: calculateWorkMinutes(formData.startTime, formData.endTime, formData.breakMinutes ?? 60),
          breakMinutes: Math.round(formData.breakMinutes ?? 60),
          restDayCount: Math.round(formData.restDayCount ?? 0),
        };
        const response = await shiftService.create(createData);
        if (response.success) {
          setShowModal(false);
          loadShifts();
        } else {
          setError(response.message || t('shiftsPage.createError'));
        }
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'İşlem sırasında bir hata oluştu';
      console.error('Shift submit error:', err?.response?.data || err);
      setError('Vardiya kaydedilemedi: ' + msg);
    }
  };

  const confirmDelete = (shift: Shift) => {
    setDeletingShift(shift);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!deletingShift) return;
    try {
      const response = await shiftService.delete(deletingShift.id);
      if (response.success) {
        setShowDeleteConfirm(false);
        setDeletingShift(null);
        loadShifts();
      } else {
        setError(response.message || t('shiftsPage.deleteError'));
        setShowDeleteConfirm(false);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Silme sirasinda hata';
      console.error('Shift delete error:', err?.response?.data || err);
      setError('Silme basarisiz: ' + msg);
      setShowDeleteConfirm(false);
    }
  };

  const openEmployeeModal = async (shift: Shift) => {
    setSelectedShift(shift);
    setShowEmployeeModal(true);
    setLoadingEmployees(true);
    try {
      const response = await shiftService.getEmployees(shift.id);
      if (response.success && response.data) {
        setShiftEmployees(response.data);
      }
    } catch (err) {
      console.error('Calisanlar yuklenemedi', err);
    } finally {
      setLoadingEmployees(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('shiftsPage.title')}</h1>
          <p className="text-slate-500 text-sm">{t('shiftsPage.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-slate-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded ${viewMode === 'card' ? 'bg-white shadow-sm' : ''}`}
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${viewMode === 'list' ? 'bg-white shadow-sm' : ''}`}
            >
              <List size={18} />
            </button>
          </div>
          <button
            onClick={openAddModal}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
          >
            <Plus size={18} /> {t('shiftsPage.newShift')}
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-600" size={20} />
          <span className="text-red-700">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {shifts.map((shift) => (
            <div
              key={shift.id}
              className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 hover:border-blue-300 transition-colors"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-3">
                  <div className="bg-indigo-50 p-2.5 rounded-lg text-indigo-600">
                    <Clock size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{shift.name}</h3>
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        shift.isActive
                          ? 'text-green-600 bg-green-50'
                          : 'text-slate-500 bg-slate-100'
                      }`}
                    >
                      {shift.isActive ? t('shiftsPage.active') : t('shiftsPage.inactive')}
                    </span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => openEditModal(shift)}
                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => confirmDelete(shift)}
                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg">
                  <div className="text-center w-1/2 border-r border-slate-200">
                    <div className="text-xs text-slate-500 uppercase font-semibold">{t('shiftsPage.start')}</div>
                    <div className="text-xl font-bold text-slate-800">{formatTime(shift.startTime)}</div>
                  </div>
                  <div className="text-center w-1/2">
                    <div className="text-xs text-slate-500 uppercase font-semibold">{t('shiftsPage.end')}</div>
                    <div className="text-xl font-bold text-slate-800">{formatTime(shift.endTime)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-slate-500 block">{t('shiftsPage.workDuration')}</span>
                    <span className="font-medium text-slate-700">{formatDuration(shift.startTime, shift.endTime)}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 block">{t('shiftsPage.employeeCount')}</span>
                    <span className="font-medium text-slate-700">{shift.employeeCount} {t('shiftsPage.person')}</span>
                  </div>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-slate-100">
                <button
                  onClick={() => openEmployeeModal(shift)}
                  className="w-full text-sm text-blue-600 font-medium py-2 rounded hover:bg-blue-50 flex items-center justify-center gap-2"
                >
                  <Users size={16} /> {t('shiftsPage.viewEmployees')}
                </button>
              </div>
            </div>
          ))}

          {/* Add New Card */}
          <button
            onClick={openAddModal}
            className="border-2 border-dashed border-slate-300 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 hover:border-blue-400 hover:text-blue-500 transition-all min-h-[250px]"
          >
            <Plus size={40} className="mb-2" />
            <span className="font-medium">{t('shiftsPage.addNewShift')}</span>
          </button>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.shiftNameCol')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.startTime')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.endTime')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.durationCol')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.employeeCol')}</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.statusCol')}</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-slate-500 uppercase">{t('shiftsPage.actionsCol')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {shifts.map((shift) => (
                <tr key={shift.id} className="hover:bg-slate-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-indigo-50 p-2 rounded-lg text-indigo-600">
                        <Clock size={18} />
                      </div>
                      <span className="font-medium text-slate-800">{shift.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-600 font-mono">{formatTime(shift.startTime)}</td>
                  <td className="px-6 py-4 text-slate-600 font-mono">{formatTime(shift.endTime)}</td>
                  <td className="px-6 py-4 text-slate-600">{formatDuration(shift.startTime, shift.endTime)}</td>
                  <td className="px-6 py-4">
                    <button
                      onClick={() => openEmployeeModal(shift)}
                      className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <Users size={14} /> {shift.employeeCount}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                        shift.isActive
                          ? 'text-green-600 bg-green-50'
                          : 'text-slate-500 bg-slate-100'
                      }`}
                    >
                      {shift.isActive ? t('shiftsPage.active') : t('shiftsPage.inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEditModal(shift)}
                        className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => confirmDelete(shift)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-800">
                {editingShift ? 'Vardiya Düzenle' : 'Yeni Vardiya'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Vardiya Adı *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Örnek: Sabah Vardiyası"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Başlangıç Saati *</label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleTimeChange('startTime', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Bitiş Saati *</label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleTimeChange('endTime', e.target.value)}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Çalışma Süresi</label>
                <div className="w-full border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-slate-700 font-medium">
                  {formData.startTime && formData.endTime
                    ? formatDuration(formData.startTime, formData.endTime)
                    : '-'}
                </div>
                <p className="text-xs text-slate-400 mt-1">Başlangıç ve bitiş saatine göre otomatik hesaplanır</p>
              </div>

              {/* GECE VARDIYASI */}
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <input
                  type="checkbox"
                  id="crossesMidnight"
                  checked={!!formData.crossesMidnight}
                  onChange={(e) => setFormData({ ...formData, crossesMidnight: e.target.checked })}
                  className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <div className="flex-1">
                  <label htmlFor="crossesMidnight" className="text-sm font-semibold text-slate-700 cursor-pointer">Gece Vardiyası</label>
                  <p className="text-xs text-slate-500 mt-0.5">Vardiya gece yarısını aşıyorsa işaretle. Örn: 17:00 → 08:00 (ertesi gün). Bitiş başlangıçtan küçükse otomatik seçilir.</p>
                </div>
              </div>

              {/* DINLENME GUN SAYISI */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Dinlenme Gün Sayısı</label>
                <input
                  type="number"
                  min={0}
                  max={7}
                  value={formData.restDayCount ?? 0}
                  onChange={(e) => setFormData({ ...formData, restDayCount: parseInt(e.target.value) || 0 })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">Sonraki vardiyaya kaç gün ara. <strong>0</strong> = ertesi gün tekrar, <strong>2</strong> = 2 gün dinlenip 3. gün başla (12/24, 24/48 rotasyonlar için).</p>
              </div>

              {/* MOLA */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mola / Ara Dinlenmesi (dakika)</label>
                <input
                  type="number"
                  min={0}
                  max={240}
                  value={formData.breakMinutes ?? 60}
                  onChange={(e) => setFormData({ ...formData, breakMinutes: parseInt(e.target.value) || 0 })}
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">İş Kanunu Madde 68: ≤4 saat → 15dk, 4-7,5 saat → 30dk, &gt;7,5 saat → 60dk. Puantaj ve bordroda net çalışma süresinden düşülür.</p>

                {/* 2026-08-14: Kanuni asgari mola kontrolu + net sure onizlemesi.
                    Molanin kanunen calisma suresinden dusulmesi zorunlu (md.68 son fikra),
                    bu yuzden kullanici kaydetmeden once net degeri gormeli. */}
                {(() => {
                  const brut = calculateWorkMinutes(formData.startTime, formData.endTime, 0);
                  if (brut <= 0) return null;
                  const mola = formData.breakMinutes ?? 60;
                  const asgari = brut <= 240 ? 15 : brut <= 450 ? 30 : 60;
                  const net = brut - mola;
                  const s = (dk: number) => `${Math.floor(dk / 60)} sa ${dk % 60} dk`;
                  return (
                    <div className="mt-2 space-y-2">
                      <div className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-700">
                        Brüt <strong>{s(brut)}</strong> − mola <strong>{mola} dk</strong> ={' '}
                        <strong className="text-blue-700">net {s(net > 0 ? net : 0)}</strong> mesai
                      </div>
                      {mola < asgari && (
                        <div className="text-xs flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-amber-800">
                          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                          <span>
                            {s(brut)} brüt çalışma için kanuni asgari ara dinlenmesi <strong>{asgari} dakikadır</strong>{' '}
                            (İş Kanunu md.68). Girilen {mola} dk bunun altında.
                          </span>
                        </div>
                      )}
                      {brut > 660 && (
                        <div className="text-xs flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 text-amber-800">
                          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                          <span>Günlük çalışma süresi 11 saati aşamaz (İş Kanunu md.63). Brüt süre {s(brut)}.</span>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <Save size={18} /> Kaydet
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Employee List Modal */}
      {showEmployeeModal && selectedShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">{selectedShift.name}</h2>
                <p className="text-sm text-slate-500">{t('shiftsPage.assignedEmployees')}</p>
              </div>
              <button
                onClick={() => setShowEmployeeModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {loadingEmployees ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : shiftEmployees.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users size={48} className="mx-auto mb-2 opacity-30" />
                  <p>{t('shiftsPage.noAssignedEmployees')}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {shiftEmployees.map((emp) => (
                    <div
                      key={emp.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-slate-800">{emp.fullName}</div>
                        <div className="text-sm text-slate-500">{emp.departmentName}</div>
                      </div>
                      <div className="text-xs text-slate-400">
                        {new Date(emp.startDate).toLocaleDateString('tr-TR')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingShift && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="text-red-600" size={24} />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">{t('shiftsPage.deleteShift')}</h3>
              <p className="text-slate-500 mb-6">
                {t('shiftsPage.deleteConfirm')} <strong>{deletingShift.name}</strong>?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
                >
                  {t('shiftsPage.cancel')}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  {t('shiftsPage.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
        <div>
          <h4 className="font-semibold text-amber-800 text-sm">{t('shiftsPage.shiftPlanningTip')}</h4>
          <p className="text-amber-700 text-sm mt-1">
            {t('shiftsPage.shiftPlanningDesc')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Shifts;
